import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { addDays, eachDayOfInterval, parseISO, startOfDay } from "date-fns";
import { ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLink } from "@/components/shared/PageHeader";
import { fetchCalendar } from "@/lib/api";
import { fmtDate, toISODate } from "@/lib/format";
import { cn } from "@/lib/utils";

const WEEKS = 14;

/** Next free Friday/Saturday evenings for a room, straight from the live calendar. */
export const FreeDates = ({ roomId = "king-oscar-amfi", roomName = "amfiet", count = 6, className }) => {
  const today = startOfDay(new Date());
  const from = toISODate(today);
  const to = toISODate(addDays(today, WEEKS * 7));
  const { data, isLoading, isError } = useQuery({ queryKey: ["calendar", from, to, roomId], queryFn: () => fetchCalendar({ from, to, room_id: roomId }) });
  const dates = useMemo(() => {
    const busy = new Set();
    for (const e of data?.entries || []) {
      try { eachDayOfInterval({ start: parseISO(e.start_date), end: parseISO(e.end_date || e.start_date) }).forEach((d) => busy.add(toISODate(d))); } catch { /* ignore */ }
    }
    const out = [];
    for (let i = 7; i <= WEEKS * 7 && out.length < count; i += 1) {
      const d = addDays(today, i);
      const dow = d.getDay();
      if ((dow === 5 || dow === 6) && !busy.has(toISODate(d))) out.push(d);
    }
    return out;
  }, [data, today, count]);

  return (
    <div className={cn(className)} data-testid="arranger-free-dates">
      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">{Array.from({ length: count }).map((_, i) => <Skeleton key={i} className="h-[104px] rounded-[14px]" />)}</div>
      ) : isError || !dates.length ? (
        <p className="text-[15px] text-muted-foreground" data-testid="arranger-free-dates-empty">Vi fann ingen ledige helgekveldar i {roomName} dei neste {WEEKS} vekene – men kvardagar og andre rom er ofte ledige. <Link to="/kalender" className="u-link font-medium text-foreground">Sjå kalenderen</Link>.</p>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {dates.map((d) => {
            const iso = toISODate(d);
            return (
              <li key={iso}>
                <Link to={`/arranger/soknad?rom=${roomId}&dato=${iso}`} className="group flex h-full flex-col justify-between rounded-[14px] border border-border bg-card p-4 transition-colors hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2" data-testid={`free-date-${iso}`}>
                  <span className="t-mono text-muted-foreground">{fmtDate(iso, "EEEE")}</span>
                  <span className="mt-3 flex items-end justify-between gap-2">
                    <span><span className="block font-display text-[30px] font-semibold leading-none tracking-[-0.03em] tabular">{fmtDate(iso, "d.")}</span><span className="mt-1 block text-[13px] capitalize text-muted-foreground">{fmtDate(iso, "MMMM")}</span></span>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary transition-colors group-hover:bg-foreground group-hover:text-background"><ArrowRight className="h-3.5 w-3.5" /></span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
        <ArrowLink to={`/kalender?rom=${roomId}`} data-testid="arranger-calendar-link">Sjå heile kalenderen</ArrowLink>
        <ArrowLink to="/arranger/soknad" data-testid="arranger-other-date-link">Vel ein annan dato eller eit anna rom</ArrowLink>
      </div>
    </div>
  );
};
